import { z } from 'zod';

export const REDISTRIBUTION_RULE_STATUSES = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
  // Only ever produced by the API, for a supplier template whose per-supplier overrides disagree.
  // The generic rule has no suppliers, so the API rejects it there.
  INTERMEDIATE: 'INTERMEDIATE',
} as const;

export type RedistributionRuleStatus =
  (typeof REDISTRIBUTION_RULE_STATUSES)[keyof typeof REDISTRIBUTION_RULE_STATUSES];

// Shared by the wire and the form: a rule can be intermediate, which a boolean cannot express.
export const redistributionBusinessRulesSchema = z.object({
  autoValidationEnabled: z.enum(REDISTRIBUTION_RULE_STATUSES),
  internalOrderValidation: z.enum(REDISTRIBUTION_RULE_STATUSES),
  externalOrderValidation: z.enum(REDISTRIBUTION_RULE_STATUSES),
  directStoreDelivery: z.enum(REDISTRIBUTION_RULE_STATUSES),
  internalDirectStoreDelivery: z.enum(REDISTRIBUTION_RULE_STATUSES),
  externalDirectStoreDelivery: z.enum(REDISTRIBUTION_RULE_STATUSES),
  sendOrderToDms: z.enum(REDISTRIBUTION_RULE_STATUSES),
  dmsAutomaticSending: z.enum(REDISTRIBUTION_RULE_STATUSES),
  dmsManualSending: z.enum(REDISTRIBUTION_RULE_STATUSES),
});

const isDisabled = (status: RedistributionRuleStatus) =>
  status === REDISTRIBUTION_RULE_STATUSES.DISABLED;

export const redistributionBusinessRulesFormSchema =
  redistributionBusinessRulesSchema
    // Mirrors RedistributionBusinessRules::validateConsistency() on the API side: the widget makes
    // these states unreachable, this catches any payload built another way. An INTERMEDIATE parent
    // is not off, so its sub-rules may legitimately be on.
    .superRefine((businessRules, context) => {
      if (
        isDisabled(businessRules.autoValidationEnabled) &&
        (!isDisabled(businessRules.internalOrderValidation) ||
          !isDisabled(businessRules.externalOrderValidation))
      ) {
        context.addIssue({
          code: 'custom',
          path: ['autoValidationEnabled'],
          message:
            'app.redistribution.business_rules.errors.order_validation_requires_auto_validation',
        });
      }

      if (
        isDisabled(businessRules.directStoreDelivery) &&
        (!isDisabled(businessRules.internalDirectStoreDelivery) ||
          !isDisabled(businessRules.externalDirectStoreDelivery))
      ) {
        context.addIssue({
          code: 'custom',
          path: ['directStoreDelivery'],
          message:
            'app.redistribution.business_rules.errors.store_delivery_requires_direct_delivery',
        });
      }

      if (
        isDisabled(businessRules.sendOrderToDms) &&
        (!isDisabled(businessRules.dmsAutomaticSending) ||
          !isDisabled(businessRules.dmsManualSending))
      ) {
        context.addIssue({
          code: 'custom',
          path: ['sendOrderToDms'],
          message:
            'app.redistribution.business_rules.errors.dms_sending_requires_dms_enabled',
        });
      }

      if (
        businessRules.dmsAutomaticSending ===
          REDISTRIBUTION_RULE_STATUSES.ENABLED &&
        businessRules.dmsManualSending === REDISTRIBUTION_RULE_STATUSES.ENABLED
      ) {
        context.addIssue({
          code: 'custom',
          path: ['dmsAutomaticSending'],
          message:
            'app.redistribution.business_rules.errors.dms_sending_modes_exclusive',
        });
      }
    });

export const REDISTRIBUTION_BUSINESS_RULES_DEFAULTS = {
  autoValidationEnabled: REDISTRIBUTION_RULE_STATUSES.ENABLED,
  internalOrderValidation: REDISTRIBUTION_RULE_STATUSES.ENABLED,
  externalOrderValidation: REDISTRIBUTION_RULE_STATUSES.DISABLED,
  directStoreDelivery: REDISTRIBUTION_RULE_STATUSES.ENABLED,
  internalDirectStoreDelivery: REDISTRIBUTION_RULE_STATUSES.ENABLED,
  externalDirectStoreDelivery: REDISTRIBUTION_RULE_STATUSES.DISABLED,
  sendOrderToDms: REDISTRIBUTION_RULE_STATUSES.ENABLED,
  dmsAutomaticSending: REDISTRIBUTION_RULE_STATUSES.ENABLED,
  dmsManualSending: REDISTRIBUTION_RULE_STATUSES.DISABLED,
} as const;

export const redistributionGenericRuleFormSchema = z.object({
  businessRules: redistributionBusinessRulesFormSchema,
});

export const redistributionGenericRulePayload = z.object({
  businessRules: redistributionBusinessRulesSchema,
});

export const redistributionGenericRuleSchema =
  redistributionGenericRulePayload.extend({
    id: z.string(),
    name: z.string(),
  });
